import { Steps } from '@noorden-insurtech/alba-design-system';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
} from 'react';
import {
  getProductConfig,
  type QuoteWizardFlow,
} from '@/app/config/products.config';
import { RequiredTooltipProvider } from '@/shared/components/required-field-tooltip';
import { useQuoteSchemas } from '@/features/quotes-schemas/hooks';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { NewQuoteFormSkeleton } from '@/features/quotes/components';
import { isCapitalGlobalCalculatedQuoteViewMode } from '@/features/quotes/components/capital-global/capital-global-quote-product.util';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import { StepCoverages } from './steps/step-coverages.component';
import { StepCommercialization } from './steps/step-commercialization.component';
import { StepCalculation } from './steps/step-calculation.component';
import type { CapitalGlobalQuoteStepOnNextResult } from './capital-global-quote-step.types';
import { StepDadosDoRisco } from './steps/step-dados-do-risco.component';
import { StepDadosDoSeguro } from './steps/step-dados-do-seguro.component';

type StepProps = {
  /** Após create, recebe `{ policyId, quoteId? }`; após attach, chamado sem argumento. */
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  onNext: (createResult?: CapitalGlobalQuoteStepOnNextResult) => void;
  onBack: () => void;
  onResumeEdit?: () => void;
  canGoNext?: boolean;
  canGoBack?: boolean;
  isViewMode?: boolean;
};

const STEP_REGISTRY: Record<string, ComponentType<StepProps>> = {
  CLIENT_INFO: StepDadosDoSeguro,
  RISK_BUSINESS: StepDadosDoRisco,
  COVERAGES: StepCoverages,
  COMMERCIALIZATION: StepCommercialization,
  CALCULATION: StepCalculation,
};

type Props = {
  productId: string;
  category: string;
  productCode: string;
  stepCode: string;
  quoteFlow?: QuoteWizardFlow;
};

export function NewCapitalGlobalForm({
  productId,
  category,
  productCode,
  stepCode,
  quoteFlow = 'new',
}: Props) {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const { id, policyId, setParams } = useQuoteParams();
  const hasNavigatedToId = useRef(false);
  const resolvedPolicyId =
    (typeof params.id === 'string' ? params.id.trim() : '') ||
    id?.trim() ||
    policyId?.trim() ||
    '';
  const { data: proposalDetails } = useProposalDetails(
    resolvedPolicyId || undefined
  );
  const isCalculatedQuoteView = isCapitalGlobalCalculatedQuoteViewMode({
    statusPortal: proposalDetails?.statusPortal,
    quoteFlow,
  });

  useEffect(() => {
    const urlId = typeof params.id === 'string' ? params.id.trim() : '';
    if (!urlId) return;
    if (id?.trim() || policyId?.trim()) return;
    setParams({ policyId: urlId, id: urlId });
  }, [params.id, id, policyId, setParams]);

  const { data: schema, isLoading } = useQuoteSchemas(
    getProductConfig(productCode)?.schemaCategory ?? category
  );

  const steps = schema?.steps ?? [];
  const activeCode = stepCode || steps[0]?.code || '';
  const activeIndex = useMemo(
    () => steps.findIndex(s => s.code === activeCode),
    [steps, activeCode]
  );
  const safeIndex = Math.max(activeIndex, 0);

  const baseSearch = useMemo(
    () => ({
      productId: productId || '',
      category: category || '',
      productCode: productCode || '',
      // O schema da rota declara `quoteFlow` obrigatório e o `validateSearch`
      // já cairia em 'new'; explicitar aqui não muda comportamento e evita
      // montar um search parcial.
      quoteFlow: (quoteFlow === 'edit' ? 'edit' : 'new') as QuoteWizardFlow,
    }),
    [productId, category, productCode, quoteFlow]
  );

  const navigateToStep = useCallback(
    (
      _nextCode: string,
      search: typeof baseSearch & { stepCode: string },
      replaceNav = false
    ) => {
      const policyRowId =
        (typeof params.id === 'string' && params.id.trim()) ||
        id?.trim() ||
        policyId?.trim() ||
        '';
      if (policyRowId) {
        navigate({
          to: '/quotes/products/new/$id',
          params: { id: policyRowId },
          search,
          replace: replaceNav,
          viewTransition: false,
        }).catch(() => {});
      } else {
        navigate({
          to: '/quotes/products/new',
          search,
          replace: replaceNav,
          viewTransition: false,
        }).catch(() => {});
      }
    },
    [navigate, params.id, id, policyId]
  );

  const goToStep = useCallback(
    (nextCode: string, replaceNav = false) => {
      navigateToStep(nextCode, { ...baseSearch, stepCode: nextCode }, replaceNav);
    },
    [navigateToStep, baseSearch]
  );

  const goToStepInEditMode = useCallback(
    (nextCode: string, replaceNav = false) => {
      navigateToStep(
        nextCode,
        {
          productId: productId || '',
          category: category || '',
          productCode: productCode || '',
          quoteFlow: 'edit',
          stepCode: nextCode,
        },
        replaceNav
      );
    },
    [navigateToStep, productId, category, productCode]
  );

  const goToStepInViewMode = useCallback(
    (nextCode: string, replaceNav = false) => {
      navigateToStep(
        nextCode,
        {
          productId: productId || '',
          category: category || '',
          productCode: productCode || '',
          quoteFlow: 'view' as QuoteWizardFlow,
          stepCode: nextCode,
        },
        replaceNav
      );
    },
    [navigateToStep, productId, category, productCode]
  );

  useEffect(() => {
    if (!steps.length) return;
    if (activeCode === stepCode) return;
    goToStep(activeCode, true);
  }, [steps.length, activeCode, stepCode, goToStep]);

  const handleNext = (createResult?: CapitalGlobalQuoteStepOnNextResult) => {
    if (!steps.length) return;
    const next = steps[activeIndex + 1];
    if (!next) return;

    const policyIdArg = createResult?.policyId;
    const quoteIdArg = createResult?.quoteId;

    if (policyIdArg && !hasNavigatedToId.current) {
      setParams({
        policyId: policyIdArg,
        id: policyIdArg,
        ...(quoteIdArg?.trim() ? { proposalNumber: quoteIdArg.trim() } : {}),
      });
      hasNavigatedToId.current = true;
      navigate({
        to: '/quotes/products/new/$id',
        params: { id: policyIdArg },
        search: { ...baseSearch, stepCode: next.code },
        replace: true,
        viewTransition: false,
      }).catch(() => {});
      return;
    }

    goToStep(next.code, false);
  };

  const handleBack = () => {
    if (isCalculatedQuoteView) return;

    const prev = steps[activeIndex - 1];
    if (!prev) return;

    if (quoteFlow === 'view') {
      goToStepInViewMode(prev.code, false);
      return;
    }

    goToStep(prev.code, false);
  };

  const handleResumeEdit = () => {
    if (isCalculatedQuoteView) return;

    const first = steps[0];
    if (!first) return;
    goToStepInEditMode(first.code, false);
  };

  if (isLoading) return <NewQuoteFormSkeleton />;

  const isViewMode = quoteFlow === 'view';
  const ActiveStep = STEP_REGISTRY[activeCode] ?? StepDadosDoSeguro;
  const canGoBack = activeIndex > 0 && !isCalculatedQuoteView;
  const canGoNext = activeIndex < steps.length - 1;

  return (
    <div className='flex flex-col gap-8'>
      <Steps.Root step={safeIndex} count={steps.length} linear>
        <Steps.List>
          {steps.map((step, index) => (
            <Steps.Item key={step.code} index={index}>
              <Steps.Trigger className='pointer-events-none cursor-default'>
                <Steps.Indicator />
                <Steps.Title>{step.stepName}</Steps.Title>
              </Steps.Trigger>
              {index < steps.length - 1 && <Steps.Separator />}
            </Steps.Item>
          ))}
        </Steps.List>
      </Steps.Root>

      <div className='relative isolate w-full bg-white rounded-2xl p-8 shadow-md flex flex-col gap-8'>
        <RequiredTooltipProvider restartKey={activeCode}>
          <ActiveStep
            onNext={handleNext}
            onBack={handleBack}
            onResumeEdit={handleResumeEdit}
            canGoNext={canGoNext}
            canGoBack={canGoBack}
            isViewMode={isViewMode}
          />
        </RequiredTooltipProvider>
      </div>
    </div>
  );
}
